import { SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { FormInstance } from 'antd/lib';
import { Dialog } from 'antd-mobile';
import { Icon } from '@fuxi/eevee-icon';
import { klona } from 'klona';
import { THERAPY_TYPE_2_TEXT } from '@/pages/ai-consultation/utils';
import { storagePrefixV2 } from '@/constant';
import { PainRelief, BodyAdjust, WeightLoss, PhysicalImprove } from './components';

import cx from './index.module.scss';

const AI_IMG = storagePrefixV2 + '/ai-detetion.png';

export const DIMENSION_TYPE_SORT: AIDiagnose.DimensionType[] = [
  'waistCircumference',
  'backThickness',
  'leftArmCircumference',
  'rightArmCircumference',
  'leftThighCircumference',
  'rightThighCircumference',
  'leftCalfCircumference',
  'rightCalfCircumference',
];

interface AIConsultationInfoProps {
  form: FormInstance<any>;
  customerInfo: GetCustomerProfileResponse;
  handleUpdateFormProblems: VoidFunc;
  hopeTypesSort?: AIDiagnose.TherapyType[];
  setHopeTypesSort?: React.Dispatch<SetStateAction<AIDiagnose.TherapyType[]>>;
  handleNavAiConsultation: VoidFunc;
}

const THERAPY_TYPE_SORT_EDIT = ['physicalImprove', 'bodyAdjust', 'weightLoss', 'painRelief'];

const AIConsultationInfo = (props: AIConsultationInfoProps) => {
  const { form, customerInfo, handleUpdateFormProblems, hopeTypesSort, setHopeTypesSort, handleNavAiConsultation } =
    props;

  const planTitleName = useMemo(() => {
    if (!customerInfo?.hopeTypes?.length) return '';

    if (customerInfo?.hopeTypes?.length > 1) {
      return '身体综合改善计划';
    } else {
      return THERAPY_TYPE_2_TEXT[customerInfo?.hopeTypes?.[0]] + '计划';
    }
  }, [customerInfo?.hopeTypes]);

  // 多个维度排序
  const newWeightLossProblems = useMemo(() => {
    const newProblems = klona(customerInfo?.weightLossList);
    return (
      newProblems?.sort(
        (a, b) => DIMENSION_TYPE_SORT?.indexOf(a?.problem) - DIMENSION_TYPE_SORT?.indexOf(b?.problem)
      ) || []
    );
  }, [customerInfo?.weightLossList]);

  const handleDelete = useCallback(
    (value: Diagnose, therapyType: AIDiagnose.TherapyType) => {
      Dialog.confirm({
        destroyOnClose: true,
        title: `是否要删除当前理疗诉求？`,
        content: '删除后已填写的内容将不会保留',
        confirmText: '确认',
        onConfirm: () => {
          form.setFieldValue(value, null);
          handleUpdateFormProblems();
          setHopeTypesSort(preVal => {
            const newVal = klona(preVal);
            const index = newVal?.indexOf(therapyType);
            if (index !== -1) {
              newVal.splice(index, 1);
            }
            return newVal;
          });
        },
      });
    },
    [form, handleUpdateFormProblems, setHopeTypesSort]
  );

  useEffect(() => {
    const newHopeTypes = klona(customerInfo?.hopeTypes);
    setHopeTypesSort(
      newHopeTypes?.sort((a, b) => THERAPY_TYPE_SORT_EDIT?.indexOf(a) - THERAPY_TYPE_SORT_EDIT?.indexOf(b)) || []
    );
  }, [customerInfo?.hopeTypes, setHopeTypesSort]);

  return (
    <div className={cx('ai-consultation-info')}>
      {hopeTypesSort?.length > 0 && <div className={cx('title')}>{planTitleName}</div>}

      <div className={cx('content', { 'no-content': !hopeTypesSort?.length })}>
        {hopeTypesSort?.map(item => (
          <div className={cx('problem-list')} key={item}>
            <div className={cx('value')}>
              {item === 'painRelief' ? (
                <PainRelief painReliefListProblems={customerInfo?.painReliefList} handleDelete={handleDelete} />
              ) : item === 'bodyAdjust' ? (
                <BodyAdjust bodyAdjustListProblems={customerInfo?.bodyAdjustList} handleDelete={handleDelete} />
              ) : item === 'weightLoss' ? (
                <WeightLoss weightLossProblems={newWeightLossProblems} handleDelete={handleDelete} />
              ) : item === 'physicalImprove' ? (
                <PhysicalImprove
                  physicalImproveListProblems={customerInfo?.physicalImproveList}
                  handleDelete={handleDelete}
                />
              ) : null}
            </div>
          </div>
        ))}
      </div>
      <div className={cx('bottom')} onClick={handleNavAiConsultation}>
        <img src={AI_IMG} className={cx('img')} />
        <span>去AI问询更新理疗诉求</span>
        <Icon name="arrow-right" className={cx('icon')} />
      </div>
    </div>
  );
};

export { AIConsultationInfo };
